import '../styles/ImplementationBaseContainer.scss';

import { Empty } from '@whistler-ui/k2/components/Empty';
import { getUserFacingErrorMessage } from '@whistler-ui/k2/helpers/userFacingError';
import { useAppMessages, useClassHelper } from '@whistler-ui/k2/hooks';
import { WebSheetTag } from '@whistler-ui/k2/types';
import { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetWebsheetSettingsByBrandId } from '../../../hooks';
import { useWebsheetConfigStore } from '../../../store/websheet/websheet-config';
import { BrandResponse } from '../../../types';
import { ImplementationPagePropsType } from '../types';
import { ImplementationSteps } from './ImplementationSteps';
import { PreviewContainer } from './PreviewContainer';

const componentName = 'c-ImplementationBaseContainer';

interface Props
  extends Omit<ImplementationPagePropsType<BrandResponse>, 'tag'> {
  tag?: WebSheetTag;
}

export const ImplementationBaseContainer = ({
  brand,
  callbacks,
  tag,
  template,
  pages,
  templateType,
}: Props) => {
  const [setMessages] = useAppMessages();
  const { t } = useTranslation(['pages', 'common']);
  const c = useClassHelper(componentName);
  const { values } = useWebsheetConfigStore();

  const {
    data: settings = null,
    isLoading = false,
    isError = false,
    error = null,
  } = useGetWebsheetSettingsByBrandId({
    environmentId: brand.environment_id,
    brandId: brand.id,
    tag: tag,
  });

  useEffect(() => {
    if (error) {
      setMessages({
        type: useAppMessages.type.ERROR,
        msg: getUserFacingErrorMessage(error, 'common:errors.default'),
      });
    }
  }, [error]);

  const { emptyUseCaseSettings } = settings ?? {};

  return (
    <section className={componentName}>
      {isError ? (
        <div className={c('__error-wrapper')}>
          <Empty.State title={t('common:errors.default')} />
        </div>
      ) : (
        <Fragment>
          <ImplementationSteps
            brand={brand}
            callbacks={callbacks}
            tag={tag}
            template={template}
            pages={pages}
            loading={!tag ? false : isLoading}
            settings={settings}
            stepsDisabled={emptyUseCaseSettings}
          />
          <PreviewContainer
            brand={brand}
            tag={tag}
            templateType={templateType}
            loading={!tag ? false : isLoading}
            settings={settings}
            deviceType={values.deviceType}
            emptyUseCaseSettings={emptyUseCaseSettings}
          />
        </Fragment>
      )}
    </section>
  );
};
